// Google Ads click id (gclid / gbraid / wbraid), saved on the booking so we can
// see which bookings came from an advert without relying on Google's tag —
// which ad blockers, Brave and Safari's cookie limits often stop.
//
// It is an advertising identifier, so it sits under the same consent as GTM:
// read from the landing URL into memory, written to storage and sent with a
// booking only once the visitor has accepted (or GTM was allowed to load).

import { CONSENT_CHANGED_EVENT, hasAnalyticsConsent } from '../config/consent';

const STORAGE_KEY = 'rs_ad_click';
const PARAMS = ['gclid', 'gbraid', 'wbraid'];
const ID_RE = /^[A-Za-z0-9_-]{10,255}$/;
// Google's default click-through conversion window.
export const AD_CLICK_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

let pending = null;
let listening = false;

function allowed() {
  return hasAnalyticsConsent() || window.__rsGtmLoaded === true;
}

function persist(record) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable — the in-memory copy still covers this visit.
  }
}

function clearStored() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Parse the click id out of a query string, or null. */
export function parseAdClick(search) {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return null;
  }
  for (const type of PARAMS) {
    const id = params.get(type);
    if (id && ID_RE.test(id)) return { type, id };
  }
  return null;
}

function onConsentChanged(event) {
  const status = event?.detail?.status;
  if (status === 'granted' && pending) persist(pending);
  if (status === 'denied') {
    pending = null;
    clearStored();
  }
}

/** Call once at start-up. A newer click replaces an older one. */
export function captureAdClick(search = window.location.search, now = Date.now()) {
  const click = parseAdClick(search);
  if (click) {
    pending = { ...click, ts: now };
    if (allowed()) persist(pending);
  }
  if (!listening) {
    listening = true;
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);
  }
}

/** The click id to attach to a new booking, or null. */
export function getAdClickForBooking(now = Date.now()) {
  if (!allowed()) return null;
  let record = pending;
  if (!record) {
    try {
      record = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch {
      record = null;
    }
  }
  if (!record || !PARAMS.includes(record.type) || !ID_RE.test(record.id || '')) return null;
  if (!Number.isFinite(record.ts) || now - record.ts > AD_CLICK_MAX_AGE_MS) {
    clearStored();
    return null;
  }
  return { type: record.type, id: record.id };
}

/** Test hook. */
export function resetAdClickForTests() {
  pending = null;
  if (listening) {
    window.removeEventListener(CONSENT_CHANGED_EVENT, onConsentChanged);
    listening = false;
  }
  clearStored();
}
